import { Card, Flex, SimpleGrid, Stack, Text } from '@mantine/core'
import { IconExternalLink, IconEye, IconEyeOff } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useState } from 'react'
import {
	deleteAllEvent,
	deleteOneEvent,
	getListCustomEvent,
	updateCustomEventStatus,
} from 'src/apis/eventAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { EventData } from 'src/interfaces'
import { formatDate } from 'src/variable/constant'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'

enum POST_EVENT_FILTER_TYPE {
	ALL = 'ALL',
	NOT_REPEAT = 'NOT_REPEAT',
	REPEAT = 'REPEAT',
	REPEAT_ONE_TO_TWO = 'REPEAT_ONE_TO_TWO',
	PASSED = 'PASSED',
}

enum POST_EVENT_SEARCH_TYPE {
	ACTIVITY = 'ACTIVITY',
	HOST = 'HOST',
}

enum POST_EVENT_COUNTRY_FILTER {
	ALL = 'ALL',
	VIETNAM = 'VIETNAM',
	OTHER_COUNTRY = 'OTHER_COUNTRY',
}

enum POST_EVENT_SORT {
	LAST_UPDATED = 'LAST_UPDATED',
	OLDEST = 'OLDEST',
	NEWEST = 'NEWEST',
	ACTIVITY_DATE = 'ACTIVITY_DATE',
}

interface EventStatistics {
	host: number
	co_host: number
	repeat_activity: number
	no_repeat_activity: number
}

interface EventPageListProps {
	columns: MRT_ColumnDef[]
	fields: {
		value: string
		label: string
	}[]
	[key: string]: any
}

const getEventWebUrl = (eventId?: string) => {
	const webUrl = process.env.REACT_APP_WEB_URL?.replace(/\/+$/, '')

	if (!webUrl || !eventId) return ''

	return `${webUrl}/en/upcoming-event/${eventId}`
}

function EventPageList({ columns, fields }: EventPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<EventData[]>([])
	const [eventType, setEventType] = useState<POST_EVENT_FILTER_TYPE>(
		POST_EVENT_FILTER_TYPE.ALL,
	)
	const [searchType, setSearchType] = useState<POST_EVENT_SEARCH_TYPE>(
		POST_EVENT_SEARCH_TYPE.ACTIVITY,
	)
	const [searchValue, setSearchValue] = useState<string>('')
	const [sort, setSort] = useState<POST_EVENT_SORT>(
		POST_EVENT_SORT.NEWEST,
	)
	const [countryFilter, setCountryFilter] =
		useState<POST_EVENT_COUNTRY_FILTER>(
			POST_EVENT_COUNTRY_FILTER.ALL,
		)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [statistics] = useState<EventStatistics>({
		host: 0,
		co_host: 0,
		repeat_activity: 0,
		no_repeat_activity: 0,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)

	const resetPage = useCallback(() => {
		setPagination((pre) => ({
			...pre,
			pageIndex: 1,
		}))
	}, [])

	const handleGetStatistics = useCallback(async () => {
		try {
			// tạm hide
			// const res = await getCustomEventStatistics()
			// setStatistics(res?.data?.results?.object || {
			// 	host: 0,
			// 	co_host: 0,
			// 	repeat_activity: 0,
			// 	no_repeat_activity: 0,
			// })
		} catch (error) {
			openError(error)
		}
	}, [openError])

	const handleGetData = useCallback(async () => {
		let arr: EventData[] = []
		let count: number = 0
		setLoading(true)
		try {
			const res = await getListCustomEvent({
				fields: ['$all', { participants: ['type', { user: ['id', 'avatar', 'name'] }] }],
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				filter_type: eventType,
				search_type: searchType,
				search_value: searchValue.trim(),
				sort,
				country_filter: countryFilter,
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				start_time_display: item?.start_time ? dayjs(Number(item.start_time)).format(formatDate) : 'N/A',
				end_time_display: item?.end_time ? dayjs(Number(item.end_time)).format(formatDate) : 'N/A',
				created_at: dayjs(item?.created_at).format(formatDate),
				end_time: item?.end_time ? dayjs(Number(item.end_time)).format(formatDate) : 'N/A',
				// start_time: dayjs(item?.start_time).format(formatDate),
				image: item?.thumbnails?.[0] || '',
			}))
			count = res?.data?.results?.objects?.count
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)

			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
	}, [
		pagination.pageIndex,
		pagination.pageSize,
		eventType,
		searchType,
		searchValue,
		sort,
		countryFilter,
		openError,
	])

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const handleDelete = useCallback(
		async (row: any) => {
			openLoadingContext()
			try {
				const res = await deleteOneEvent(row?.original?.id)
				if (res?.data?.code === 200) {
					await handleGetData()
					await handleGetStatistics()
					openModal({ type: 'success', label: 'Deleted successfully !!!' })
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			closeLoadingContext,
			handleGetData,
			handleGetStatistics,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const handleDeleteAll = useCallback(
		async (row: any) => {
			openLoadingContext()
			try {
				const res = await deleteAllEvent(row?.original?.id)
				if (res?.data?.code === 200) {
					await handleGetData()
					await handleGetStatistics()
					openModal({ type: 'success', label: 'All Deleted successfully !!!' })
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			closeLoadingContext,
			handleGetData,
			handleGetStatistics,
			openError,
			openLoadingContext,
			openModal,
		],
	)
	const handleConfirmDelete = useCallback(
		(row: any) => {
			const options = [
				{
					label: 'only this event',
					onClick: () => {
						handleDelete(row);
					},
				}
			]

			if (row.original.repeat_type.type !== 'NONE') {
				options.push({
					label: 'all repeat events',
					onClick: () => {
						handleDeleteAll(row); // implement this function as needed
					},
				})
			}


			openModal({
				type: 'option',
				label: 'Do you want to delete?',
				options: options
			});
		},
		[handleDelete, handleDeleteAll, openModal],
	);

	const handleToggleStatus = useCallback(
		async (row: any) => {
			const currentStatus = row?.original?.status !== false
			openLoadingContext()
			try {
				const res = await updateCustomEventStatus(
					row?.original?.id,
					!currentStatus,
				)
				if (res?.data?.code === 200) {
					await handleGetData()
					await handleGetStatistics()
					openModal({
						type: 'success',
						label: currentStatus
							? 'Event hidden from users successfully !!!'
							: 'Event visible to users successfully !!!',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, handleGetData, handleGetStatistics, openError, openLoadingContext, openModal],
	)

	const handleConfirmToggleStatus = useCallback(
		(row: any) => {
			const currentStatus = row?.original?.status !== false
			openModal({
				type: 'option',
				label: currentStatus
					? 'Do you want to hide this event from users?'
					: 'Do you want to show this event to users?',
				options: [
					{
						label: currentStatus ? 'Hide' : 'Show',
						onClick: () => handleToggleStatus(row),
					},
				],
			})
		},
		[handleToggleStatus, openModal],
	)

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), eventType, searchType, sort, countryFilter])

	useEffect(() => {
		handleGetStatistics()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		let timer
		timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				handleGetData()
			} else handleChangePage(1)
		}, 500)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue])

	const statisticItems = [
		{
			label: 'Host',
			value: statistics.host,
			background: '#ECEBFD',
			border: '#C9C4F7',
			labelColor: '#4C3B9B',
			valueColor: '#35237D',
		},
		{
			label: 'Co-host',
			value: statistics.co_host,
			background: '#FEF8EC',
			border: '#F3D9A4',
			labelColor: '#8A5A13',
			valueColor: '#6F4308',
		},
		{
			label: 'Repeat Activity',
			value: statistics.repeat_activity,
			background: '#EDF9F5',
			border: '#B8E6D6',
			labelColor: '#176B55',
			valueColor: '#0E4E3E',
		},
		{
			label: 'No repeat activity',
			value: statistics.no_repeat_activity,
			background: '#EAF4FF',
			border: '#B7D9FB',
			labelColor: '#1F5F99',
			valueColor: '#164B7A',
		},
	]

	const renderCustomActions = (row: any) => {
		const link = getEventWebUrl(row?.original?.id)
		const isVisible = row?.original?.status !== false

		return (
			<Flex gap={6} align="center">
				{link && (
					<CustomTooltip label="Open Web">
						<a href={link} target="_blank" rel="noreferrer noopener">
							<span
								className={`${actionClasses.actionButton} ${actionClasses.primaryAction}`}
							>
								<IconExternalLink size={16} />
							</span>
						</a>
					</CustomTooltip>
				)}
				<CustomTooltip label={isVisible ? 'Hide from users' : 'Show to users'}>
					<button
						type="button"
						onClick={() => handleConfirmToggleStatus(row)}
						className={`${actionClasses.actionButton} ${
							isVisible
								? actionClasses.primaryAction
								: actionClasses.mutedAction
						}`}
					>
						{isVisible ? <IconEye size={16} /> : <IconEyeOff size={16} />}
					</button>
				</CustomTooltip>
			</Flex>
		)
	}
	const sectionTitleSx = { color: '#111827', fontWeight: 700 }
	const controlSx = { minWidth: 220 }

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex gap={16} align="flex-start" sx={{ flexWrap: 'wrap' }}>
				<Flex gap={8} sx={{ flex: 1, minWidth: 520, flexWrap: 'wrap' }}>
					<Stack spacing={6}>
						<Text size="sm" sx={sectionTitleSx}>Search</Text>
						<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
							<CustomSelect
								value={searchType}
								data={[
									{ label: 'Activity', value: POST_EVENT_SEARCH_TYPE.ACTIVITY },
									{ label: 'Host', value: POST_EVENT_SEARCH_TYPE.HOST },
								]}
								onChange={(e) => {
									setSearchType(e as POST_EVENT_SEARCH_TYPE)
									resetPage()
								}}
								sx={controlSx}
							/>
							<CustomClearInput
								value={searchValue}
								onChange={(e) => setSearchValue(e)}
								placeholder={'Search name, ...'}
								sx={controlSx}
							/>
						</Flex>
					</Stack>
					<Stack spacing={6}>
						<Text size="sm" sx={sectionTitleSx}>Sort</Text>
						<CustomSelect
							data={[
								{ label: 'Oldest created', value: POST_EVENT_SORT.OLDEST },
								{ label: 'Newest created', value: POST_EVENT_SORT.NEWEST },
								{ label: 'Activity date', value: POST_EVENT_SORT.ACTIVITY_DATE },
							]}
							value={sort}
							onChange={(e) => {
								setSort(e as POST_EVENT_SORT)
								resetPage()
							}}
							sx={controlSx}
						/>
					</Stack>
					<Stack spacing={6}>
						<Text size="sm" sx={sectionTitleSx}>Filter</Text>
						<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
							<CustomSelect
								data={[
									{ label: 'All countries', value: POST_EVENT_COUNTRY_FILTER.ALL },
									{ label: 'Vietnam', value: POST_EVENT_COUNTRY_FILTER.VIETNAM },
									{ label: 'Other country', value: POST_EVENT_COUNTRY_FILTER.OTHER_COUNTRY },
								]}
								value={countryFilter}
								onChange={(e) => {
									setCountryFilter(e as POST_EVENT_COUNTRY_FILTER)
									resetPage()
								}}
								sx={controlSx}
							/>
							<CustomSelect
								data={[
									{ label: 'All repeat types', value: POST_EVENT_FILTER_TYPE.ALL },
									{ label: 'Not repeat event', value: POST_EVENT_FILTER_TYPE.NOT_REPEAT },
									{ label: 'Repeat event', value: POST_EVENT_FILTER_TYPE.REPEAT },
									{ label: 'Repeat one to two', value: POST_EVENT_FILTER_TYPE.REPEAT_ONE_TO_TWO },
									{ label: 'Past event', value: POST_EVENT_FILTER_TYPE.PASSED },
								]}
								value={eventType}
								onChange={(e) => {
									setEventType(e as POST_EVENT_FILTER_TYPE)
									resetPage()
								}}
								sx={controlSx}
							/>
						</Flex>
					</Stack>
				</Flex>
				<SimpleGrid
					display={'none'}
					cols={2}
					spacing={8}
					sx={{ width: 360, '@media (max-width: 900px)': { width: '100%' } }}
				>
					{statisticItems.map((item) => (
						<Card
							key={item.label}
							withBorder
							radius={8}
							p="md"
							sx={{
								backgroundColor: item.background,
								borderColor: item.border,
								boxShadow: '0 6px 16px rgba(15, 23, 42, 0.04)',
							}}
						>
							<Text
								size="xs"
								weight={700}
								color={item.labelColor}
								transform="uppercase"
								sx={{ letterSpacing: 0.4 }}
							>
								{item.label}
							</Text>
							<Text size={28} weight={700} color={item.valueColor}>
								{item.value}
							</Text>
						</Card>
					))}
				</SimpleGrid>
			</Flex>
			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				// searchQueryValue={searchValue}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	search_type:
				// 		searchType !== POST_EVENT_SEARCH_TYPE.ACTIVITY
				// 			? searchType
				// 			: undefined,
				// 	sort: sort !== POST_EVENT_SORT.NEWEST ? sort : undefined,
				// 	country_filter:
				// 		countryFilter !== POST_EVENT_COUNTRY_FILTER.ALL
				// 			? countryFilter
				// 			: undefined,
				// 	event_type:
				// 		eventType !== POST_EVENT_FILTER_TYPE.ALL ? eventType : undefined,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				handleDelete={handleConfirmDelete}
				_renderCustomActions={renderCustomActions}
			/>
		</Stack>
	)
}

export default EventPageList
